import { useEffect } from "react";
import "./FastTooltip.css";

// One quick tooltip for the whole app (Alejo, 2026-10-10: the hover of the
// invoice icons everywhere there is a hover, e.g. the languages). Any element
// with a `title` (or `data-tip`) shows it: the browser's own tooltip waits
// about a second and can't be styled. On the first hover the `title` moves
// to `data-tip` (so the browser's doesn't show), and the element keeps an
// accessible name (`aria-label`) when it has none. Nothing to do per element.
// Not on touch screens (no hover there).

const SHOW_MS = 60;

const FastTooltip = () => {
  useEffect(() => {
    if (typeof window === "undefined" || (window.matchMedia && window.matchMedia("(hover: none)").matches)) return undefined;
    const bubble = document.createElement("div");
    bubble.className = "fastTip";
    bubble.setAttribute("role", "tooltip");
    document.body.appendChild(bubble);
    let timer = null;
    let current = null;

    const hide = () => {
      clearTimeout(timer);
      current = null;
      bubble.classList.remove("isOn");
    };
    const place = (el) => {
      const r = el.getBoundingClientRect();
      const b = bubble.getBoundingClientRect();
      let left = r.left + r.width / 2 - b.width / 2;
      left = Math.max(8, Math.min(left, window.innerWidth - b.width - 8));
      let top = r.top - b.height - 8;
      const below = top < 8;
      if (below) top = r.bottom + 8;
      bubble.dataset.pos = below ? "below" : "above";
      const mid = r.left + r.width / 2 - left;
      bubble.style.setProperty("--tip-arrow", `${Math.round(Math.max(10, Math.min(mid, b.width - 10)))}px`);
      bubble.style.left = `${Math.round(left)}px`;
      bubble.style.top = `${Math.round(top)}px`;
    };
    const tipOf = (el) => {
      const native = el.getAttribute("title");
      if (native) {
        el.setAttribute("data-tip", native);
        el.removeAttribute("title");
        if (!el.getAttribute("aria-label") && !el.textContent.trim()) el.setAttribute("aria-label", native);
      }
      return el.getAttribute("data-tip");
    };
    const over = (e) => {
      const el = e.target && e.target.closest ? e.target.closest("[title], [data-tip]") : null;
      if (!el || el === current) return;
      // No tooltip on PayPal's buttons or on file names (Alejo, 2026-10-10): the
      // browser's own `title` is taken off too, so nothing shows there.
      if (el.closest('[class*="paypal" i], [id*="paypal" i], .clFn, .ofFileName, [data-no-tip]')) {
        el.removeAttribute("title");
        return;
      }
      const text = tipOf(el);
      if (!text) return;
      hide();
      current = el;
      timer = setTimeout(() => {
        if (!document.body.contains(el)) return;
        bubble.textContent = text;
        bubble.classList.add("isOn");
        place(el);
      }, SHOW_MS);
    };
    const out = (e) => {
      if (current && (!e.relatedTarget || !current.contains(e.relatedTarget))) hide();
    };
    document.addEventListener("mouseover", over, true);
    document.addEventListener("mouseout", out, true);
    document.addEventListener("mousedown", hide, true);
    document.addEventListener("keydown", hide, true);
    window.addEventListener("scroll", hide, true);
    return () => {
      hide();
      document.removeEventListener("mouseover", over, true);
      document.removeEventListener("mouseout", out, true);
      document.removeEventListener("mousedown", hide, true);
      document.removeEventListener("keydown", hide, true);
      window.removeEventListener("scroll", hide, true);
      bubble.remove();
    };
  }, []);
  return null;
};

export default FastTooltip;
